{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            Copyright (c) 2018 - 2024                               }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.Lists;

{$DEFINE NOPP}

interface

uses
  Classes, Web, WEBLib.Controls, WEBLib.Dialogs, WEBLib.Menus;

type
  TListItem = class;
  TListItems = class;
  TListControl = class;

  TListStyle = (lsNone, lsPagination, lsTabs, lsBreadcrumb, lsListGroup, lsListGroupHorizontal, lsNavPills, lsNavPillsHorizontal, lsDropDown);

  TListGetItemClassEvent = procedure(Sender: TObject; AItem: TListItem; var AClassName: string) of object;

  TListGetItemChildrenEvent = procedure(Sender: TObject; AItem: TListItem; AElement: TJSHTMLElementRecord) of object;

  TListItemEvent = procedure(Sender: TObject; AListItem: TListItem) of object;

  TListItem = class(TCollectionItem)
  private
    FItems: TListItems;
    FLink: string;
    FLinkTarget: string;
    FText: string;
    FItemClassName: TElementClassName;
    FLinkClassName: TElementClassName;
    FTag: integer;
    FListElement: TJSElement;
    FEnabled: boolean;
    FActive: boolean;
    FAutoCollaps: boolean;
    FHint: string;
    FHTMLChilds: integer;
    FBackupElement: TJSElement;
    FBackupCollapse: boolean;
    FLinkElement: TJSElement;
    FData: string;
    FAbsIndex: integer;
    procedure SetItems(const Value: TListItems);
    procedure SetLink(const Value: string);
    procedure SetText(const Value: string);
    procedure SetActive(const Value: boolean);
    procedure SetEnabled(const Value: boolean);
    procedure SetAutoCollaps(const Value: boolean);
    procedure SetHint(const Value: string);
  protected
    procedure SubItemsChanged(Sender: TObject); virtual;
    property AbsIndex: integer read FAbsIndex write FAbsIndex;
  public
    constructor Create(Collection: TCollection); override;
    destructor Destroy; override;
    procedure Assign(Source: TPersistent); override;
    function ItemId: string;
    procedure Store;
    procedure Restore;
  public
    property ListElement: TJSElement read FListElement;
    property LinkElement: TJSElement read FLinkElement;
    procedure AddLink(const AURL: string; ATarget: string = ''); overload;
    procedure AddLink(const AURL: string; ATarget: string; AClass: string); overload;
    function IsLink: boolean;
    function IsCollapsed: boolean;
    procedure Expand;
    procedure Collapse;
    property Data: string read FData write FData;
  published
    property Active: boolean read FActive write SetActive;
    property AutoCollaps: boolean read FAutoCollaps write SetAutoCollaps;
    property Enabled: boolean read FEnabled write SetEnabled;
    property Hint: string read FHint write SetHint;
    property ItemClassName: TElementClassName read FItemClassName write FItemClassName;
    property Items: TListItems read FItems write SetItems;
    property Link: string read FLink write SetLink;
    property LinkClassName: TElementClassName read FLinkClassName write FLinkClassName;
    property LinkTarget: string read FLinkTarget write FLinkTarget;
    property Tag: integer read FTag write FTag;
    property Text: string read FText write SetText;
  end;

  TListItems = class(TOwnedCollection)
  private
    FOnChange: TNotifyEvent;
    function GetItem(Index: integer): TListItem; reintroduce;
    procedure SetItem(Index: integer; const Value: TListItem);
    function GetListControl: TListControl;
  protected
    procedure Update(Item: TCollectionItem); override;
    procedure DoChanged; virtual;
    procedure Store;
    procedure Restore;
  public
    constructor Create(AOwner: TPersistent);
    function Add: TListItem; reintroduce;
    function Insert(Index: integer): TListItem; reintroduce;
    property Items[Index: integer]: TListItem read GetItem write SetItem; default;
    property ListControl: TListControl read GetListControl;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  end;

  TListControl = class(TWebCustomControl)
  private
    FItems: TListItems;
    FElementListClassName: TElementClassName;
    FOnGetItemChildren: TListGetItemChildrenEvent;
    FOnGetItemClass: TListGetItemClassEvent;
    FOnItemDblClick: TListItemEvent;
    FOnItemClick: TListItemEvent;
    FDefaultItemClassName: string;
    FDefaultItemLinkClassName: string;
    FListStyle: TListStyle;
    FDesignTime: boolean;
    FMultiSelect: boolean;
    FAutoSelect: boolean;
    procedure SetItems(const Value: TListItems);
    procedure SetListStyle(const Value: TListStyle);
    procedure SetElementListClassName(const Value: TElementClassName);
    function GetItemIndex: integer;
    procedure SetItemIndex(const Value: integer);
  protected
    function HandleDoClick(Event: TJSMouseEvent): Boolean; override;
    function HandleDoDblClick(Event: TJSMouseEvent): Boolean; override;
    function HandleDoAnchorClick(Event: TJSEvent): Boolean; virtual;
    procedure ItemsChanged(Sender: TObject); virtual;
    procedure ItemClick(AItem: TListItem); virtual;
    procedure ItemDblClick(AItem: TListItem); virtual;
    procedure GetItemClass(Item: TListItem; var AClass: string); virtual;
    procedure GetItemChildren(Item: TListItem; AElement: TJSHTMLElement); virtual;
    function CreateElement: TJSElement; override;
    procedure UpdateElement; override;
    procedure RenderList(AElement: TJSElement; Items: TListItems; var AIndex: integer);
    function ListItemByElement(AElement: TJSElement): TListItem;
    procedure EnableDrag; override;
    procedure DisableDrag; override;
  public
    constructor Create(AOwner: TComponent); override;  overload;
    destructor Destroy; override;
    procedure CreateInitialize; override;
    function ListElement: TJSHTMLElement;
    procedure SetFilter(Condition: string; CaseSensitive: boolean = true);
    procedure RemoveFilter;
    property ItemIndex: integer read GetItemIndex write SetItemIndex;
  published
    property Align;
    property AlignWithMargins;
    property Anchors;
    property AutoSelect: boolean read FAutoSelect write FAutoSelect default false;
    property ChildOrder;
    property DefaultItemClassName: string read FDefaultItemClassName write FDefaultItemClassName;
    property DefaultItemLinkClassName: string read FDefaultItemLinkClassName write FDefaultItemLinkClassName;
    property DragMode;
    property ElementClassName;
    property ElementFont;
    property ElementID;
    property ElementPosition;
    property ElementListClassName: TElementClassName read FElementListClassName write SetElementListClassName;
    property HeightPercent;
    property HeightStyle;
    property Items: TListItems read FItems write SetItems;
    property Margins;
    property MultiSelect: boolean read FMultiSelect write FMultiSelect default false;
    property PopupMenu;
    property Style: TListStyle read FListStyle write SetListStyle;
    property Visible;
    property WidthPercent;
    property WidthStyle;

    property OnGetItemChildren: TListGetItemChildrenEvent read FOnGetItemChildren write FOnGetItemChildren;
    property OnGetItemClass: TListGetItemClassEvent read FOnGetItemClass write FOnGetItemClass;
    property OnItemClick: TListItemEvent read FOnItemClick write FOnItemClick;
    property OnItemDblClick: TListItemEvent read FOnItemDblClick write FOnItemDblClick;
    property OnDragDrop;
    property OnDragOver;
    property OnEndDrag;
    property OnStartDrag;
  end;

  TWebListControl = class(TListControl);

implementation

uses
  SysUtils, WEBLib.Utils, WEBLib.Forms;

{ TListItems }

function TListItems.Add: TListItem;
begin
  Result := TListItem(inherited Add);
end;

constructor TListItems.Create(AOwner: TPersistent);
begin
  inherited Create(AOwner, TListItem);
end;

procedure TListItems.DoChanged;
begin
  if Assigned(OnChange) then
    OnChange(Self);
end;

function TListItems.GetItem(Index: integer): TListItem;
begin
  Result := TListItem(inherited Items[Index]);
end;

function TListItems.GetListControl: TListControl;

  function FindListControl(AOwner: TPersistent): TListControl;
  begin
    Result := nil;

    if AOwner is TListControl then
      Result := AOwner as TListControl
    else
    begin
      if Assigned(AOwner) and (AOwner is TListItem) then
      begin
        Result := FindListControl((AOwner as TListItem).Collection.Owner);
      end;
    end;
  end;

begin
  Result := FindListControl(Self.Owner);
end;

function TListItems.Insert(Index: integer): TListItem;
begin
  Result := TListItem(inherited Insert(Index));
end;

procedure TListItems.Restore;
var
  i: integer;
begin
  for i := 0 to Count - 1 do
    Items[i].Restore;
end;

procedure TListItems.SetItem(Index: integer; const Value: TListItem);
begin
  inherited Items[Index] := Value;
end;

procedure TListItems.Store;
var
  i: integer;
begin
  for i := 0 to Count - 1 do
    Items[i].Store;
end;

procedure TListItems.Update(Item: TCollectionItem);
begin
  inherited;
  DoChanged;
end;

{ TListItem }

procedure TListItem.AddLink(const AURL: string; ATarget: string);
begin
  AddLink(AURL, ATarget, '');
end;

procedure TListItem.AddLink(const AURL: string; ATarget, AClass: string);
begin
  FLink := AURL;
  FLinkTarget := ATarget;
  FLinkClassName := AClass;
  Changed(false);
end;

procedure TListItem.Assign(Source: TPersistent);
begin
  if (Source is TListItem) then
  begin
    FItemClassName := (Source as TListItem).ItemClassName;
    FText := (Source as TListItem).Text;
    FLink := (Source as TListItem).Link;
    FLinkClassName := (Source as TListItem).LinkClassName;
    FLinkTarget :=  (Source as TListItem).LinkTarget;
    FItems.Assign((Source as TListItem).Items);
    FTag := (Source as TListItem).Tag;
    FEnabled := (Source as TListItem).Enabled;
    FActive := (Source as TListItem).Active;
    FHint := (Source as TListItem).Hint;
  end;
end;

procedure TListItem.Collapse;
var
  el: TJSElement;
begin
  if Assigned(FListElement) then
  begin
    el := FListElement.nextElementSibling;
    if Assigned(el) and (el.tagName = 'DIV') then
    begin
      if el.classList.contains('show') then
        el.classList.remove('show');
    end;
  end;
end;

constructor TListItem.Create(Collection: TCollection);
var
  ctrl: TListControl;
begin
  FItems := TListItems.Create(Self);
  FItems.PropName := 'Items';
  FItems.OnChange := SubItemsChanged;
  inherited;
  FActive := false;
  FEnabled := true;
  FBackupElement := nil;
  ctrl := (Collection as TListItems).ListControl;

  if Assigned(ctrl) then
  begin
    FItemClassName := ctrl.DefaultItemClassName;
    FLinkClassName := ctrl.DefaultItemLinkClassName;
  end;
end;

destructor TListItem.Destroy;
begin
  FItems.Free;
  inherited Destroy;
end;

procedure TListItem.Expand;
var
  el: TJSElement;
begin
  if Assigned(FListElement) then
  begin
    el := FListElement.nextElementSibling;
    if Assigned(el) and (el.tagName = 'DIV') then
    begin
      if not el.classList.contains('show') then
        el.classList.add('show');
    end;
  end;
end;

function TListItem.IsCollapsed: boolean;
var
  el: TJSElement;
begin
  Result := true;
  if Assigned(FListElement) then
  begin
    el := FListElement.nextElementSibling;
    if Assigned(el) and (el.tagName = 'DIV') then
    begin
      Result := not el.classList.contains('show');
    end;
  end;
end;

function TListItem.IsLink: boolean;
begin
  Result := (Link <> '') {and (LinkClassName <> '')};
end;

function TListItem.ItemId: string;
var
  itm: TListItem;

  function IsSubItem(AItem: TListItem): boolean;
  begin
    Result := false;

    if Assigned(AItem.Collection) and Assigned(AItem.Collection.Owner) then
    begin
      Result := AItem.Collection.Owner is TListItem;
    end;
  end;

begin
  Result := IntToStr(Index);

  itm := Self;

  while IsSubItem(itm) do
  begin
    itm := itm.Collection.Owner as TListItem;
    Result := Result + '_' + IntToStr(Index);
  end;
end;

procedure TListItem.Restore;
var
  i: integer;
begin
  if Assigned(FListElement) and Assigned(FBackupElement) then
  begin
    i := 0;

    while FBackupElement.childElementCount > 0 do
    begin
      if i >= FHTMLChilds then
        FListElement.appendChild(FBackupElement.firstElementChild)
      else
        FBackupElement.removeChild(FBackupElement.firstElementChild);
    end;
  end;

  if not FBackupCollapse then
    Expand;

  Items.Restore;
end;

procedure TListItem.SetActive(const Value: boolean);
begin
  FActive := Value;
  Changed(false);
end;

procedure TListItem.SetAutoCollaps(const Value: boolean);
begin
  FAutoCollaps := Value;
  Changed(false);
end;

procedure TListItem.SetEnabled(const Value: boolean);
begin
  FEnabled := Value;
  Changed(false);
end;

procedure TListItem.SetHint(const Value: string);
begin
  FHint := Value;
  Changed(false);
end;

procedure TListItem.SetItems(const Value: TListItems);
begin
  FItems.Assign(Value);
end;

procedure TListItem.SetLink(const Value: string);
begin
  FLink := Value;
  Changed(false);
end;

procedure TListItem.SetText(const Value: string);
begin
  FText := Value;
  Changed(false);
end;

procedure TListItem.Store;
begin
  if not Assigned(FBackupElement) then
    FBackupElement := document.createElement('SPAN');

  FBackupCollapse := IsCollapsed;

  if Assigned(FListElement) then
    while FListElement.childElementCount > 0 do
    begin
      FBackupElement.appendChild(FListElement.firstElementChild);
    end;

  Items.Store;
end;

procedure TListItem.SubItemsChanged(Sender: TObject);
begin
  Changed(false);
end;

{ TListControl }

constructor TListControl.Create(AOwner: TComponent);
begin
  inherited;
  TabStop := false;
  WidthStyle := ssAbsolute;
  HeightStyle := ssAbsolute;
  WidthPercent := 100;
  HeightPercent := 100;
  ElementPosition := epAbsolute;
  Style := lsNone;
end;

function TListControl.CreateElement: TJSElement;
begin
  if Style = lsDropDown then
    Result := document.createElement('UL')
  else
  begin
    Result := document.createElement('DIV');
    Result.appendChild(document.createElement('UL'));
  end;
end;

procedure TListControl.CreateInitialize;
begin
  inherited;

  FDesignTime := (csDesigning in ComponentState) and not
    ((csReading in Owner.ComponentState) or (csLoading in Owner.ComponentState));

  FItems := TListItems.Create(Self);

  Width := 280;
  Height := 240;

  if FDesignTime then
  begin
    FItems.Add.Text := 'Item 1';
    FItems.Add.Text := 'Item 2';
    FItems.Add.Text := 'Item 3';
  end;

  FItems.OnChange := ItemsChanged;
end;

destructor TListControl.Destroy;
begin
  FItems.Free;
  inherited;
end;

procedure TListControl.DisableDrag;
  procedure RemoveDraggableAttribute(AElement: TJSElement);
  var
    I: Integer;
  begin
    for I := 0 to AElement.children.length - 1 do
    begin
      if TJSElement(AElement.children[I]).tagName = 'LI' then
      begin
        TJSElement(AElement.children[I]).removeAttribute('draggable');
        RemoveDraggableAttribute(TJSElement(AElement.children[I]));
      end
      else
        RemoveDraggableAttribute(TJSElement(AElement.children[I]));
    end;
  end;
begin
  if not Assigned(Container) then
    Exit;

  RemoveDraggableAttribute(Container);
end;

procedure TListControl.EnableDrag;
  procedure AddDraggableAttribute(AElement: TJSElement);
  var
    I: Integer;
  begin
    for I := 0 to AElement.children.length - 1 do
    begin
      if TJSElement(AElement.children[I]).tagName = 'LI' then
      begin
        TJSElement(AElement.children[I]).Attrs['draggable'] := 'true';
        AddDraggableAttribute(TJSElement(AElement.children[I]));
      end
      else
        AddDraggableAttribute(TJSElement(AElement.children[I]));
    end;
  end;
begin
  if not Assigned(Container) then
    Exit;

  AddDraggableAttribute(Container);
end;

procedure TListControl.GetItemChildren(Item: TListItem;
  AElement: TJSHTMLElement);
var
  LElementRec: TJSHTMLElementRecord;
begin
  LElementRec.element := AElement;
  if Assigned(OnGetItemChildren) then
    OnGetItemChildren(Self, Item, LElementRec);
end;

procedure TListControl.GetItemClass(Item: TListItem; var AClass: string);
begin
  if Assigned(OnGetItemClass) then
    OnGetItemClass(Self, Item, AClass);
end;

function TListControl.GetItemIndex: integer;
var
  i: integer;
begin
  Result := -1;

  for i := 0 to Items.Count - 1  do
  begin
    if Items[i].Active then
    begin
      Result := i;
      break;
    end;
  end;
end;

function TListControl.HandleDoAnchorClick(Event: TJSEvent): Boolean;
var
  li: TListItem;
begin
  li := ListItemByElement(TJSElement(Event.targetElement.parentNode));

  if Assigned(li) then
    ItemClick(li);

  Result := false;
end;

function TListControl.HandleDoClick(Event: TJSMouseEvent): Boolean;
var
  li: TListItem;
begin
  if Assigned(OnItemClick) then
  begin
    event.preventDefault;
    event.stopPropagation;
  end;

  li := ListItemByElement(Event.targetElement);

  if Assigned(li) then
  begin

    if AutoSelect then
    begin
      if MultiSelect then
        li.Active := not li.Active
      else
      begin
        ItemIndex := li.AbsIndex;
      end;
    end;

    if Assigned(li) then
      ItemClick(li);
  end;

  Result := false;
end;

function TListControl.HandleDoDblClick(Event: TJSMouseEvent): Boolean;
var
  li: TListItem;
begin
  li := ListItemByElement(Event.targetElement);

  if Assigned(li) then
    ItemDblClick(li);

  Result := false;
end;

procedure TListControl.ItemClick(AItem: TListItem);
begin
  if Assigned(OnItemClick) then
    OnItemClick(Self, AItem);
end;

procedure TListControl.ItemDblClick(AItem: TListItem);
begin
  if Assigned(OnItemDblClick) then
    OnItemDblClick(Self, AItem);
end;

procedure TListControl.ItemsChanged(Sender: TObject);
var
  ul: TJSElement;
  idx: integer;
begin
  if IsUpdating then
    Exit;

  if Assigned(ElementHandle) then
  begin
    ul := ListElement;

    if Assigned(ul) then
    begin
      if Style = lsListGroup then
        Items.Store;

      while Assigned(ul.firstChild) do
        ul.removeChild(ul.firstChild);

      if ElementListClassName <> '' then
        ul.setAttribute('class', ElementListClassName);

      idx := 0;
      RenderList(ul, Items, idx);

      if Style = lsListGroup then
        Items.Restore;
    end;

    if (csDesigning in ComponentState) then
      UpdateElement;
  end;
end;

function TListControl.ListElement: TJSHTMLElement;
begin
  Result := nil;

  if Assigned(ElementHandle) then
  begin
    if (ElementHandle.TagName = 'UL') then
    begin
      Result := ElementHandle;
      Exit;
    end;

    if IsLinked then
    begin
      if ElementHandle.TagName = 'UL' then
      begin
        Result := ElementHandle;
        Exit;
      end;
    end;

    if (ElementHandle.childElementCount > 0) then
    begin
      Result := TJSHTMLElement(ElementHandle.firstChild);
    end
    else
    begin
      Result := TJSHTMLElement(document.createElement('UL'));
      ElementHandle.insertAdjacentElement('afterbegin',Result);
    end;
  end;
end;

function TListControl.ListItemByElement(AElement: TJSElement): TListItem;

  function SearchElement(AItems: TListItems): TListItem;
  var
     i: integer;
  begin
    Result := nil;

    for i := 0 to AItems.Count - 1 do
    begin

      if AItems[i].ListElement = AElement then
      begin
        Result := AItems[i];
        Break;
      end;

      if AItems[i].Items.Count > 0 then
      begin
        Result := SearchElement(AItems[i].Items);
        if Assigned(Result) then
          break;
      end;
    end;
  end;

begin
  Result := SearchElement(Items);
end;

procedure TListControl.RemoveFilter;
var
  i: integer;
  el: TJSHTMLElement;
begin
  for i := 0 to Items.Count - 1 do
  begin
     el := TJSHTMLElement(Items[i].FListElement);
     el.style.removeProperty('display');
  end;
end;

procedure TListControl.RenderList(AElement: TJSElement; Items: TListItems; var AIndex: integer);
var
  i: integer;
  ul,li,a,dv: TJSElement;
  aclass,datatoggle,datatarget: string;
  doins: boolean;
  el: TJSHTMLElement;
begin
  if not Assigned(AElement) then
    Exit;

  if Application.BootstrapVersion <> bv5 then
  begin
    datatoggle := 'data-toggle';
    datatarget := 'data-target';
  end
  else
  begin
    datatoggle := 'data-bs-toggle';
    datatarget := 'data-bs-target';
  end;

  for i := 0 to Items.Count - 1 do
    begin
      li := document.createElement('li');

      if DragMode = dmAutomatic then
        li['draggable'] := 'true';

      Items[i].FListElement := li;
      Items[i].FHTMLChilds := 0;

      aclass := Items[i].ItemClassName;

      GetItemClass(Items[i], aclass);

      if Items[i].Active and {not (Items[i].IsLink and} (Style <> lsNavPills) then
        aclass := aclass + ' active';

      if not Items[i].Enabled then
        aclass := aclass + ' disabled';

      if aclass <> '' then
        li.setAttribute('class',aclass);

      if Items[i].Hint <> '' then
        li.setAttribute('title',Items[i].Hint);

      if Items[i].IsLink or (Style = lsPagination) then
      begin
        doins := not Assigned(Items[i].LinkElement);
        if doins or not Items[i].IsLink then
          Items[i].FLinkElement := document.createElement('a');

        a := Items[i].LinkElement;

        a.innerHTML := Items[i].Text;

        aclass := Items[i].LinkClassName;
        if Items[i].Active then
          aclass := aclass + ' active';

        if (Items[i].LinkClassName <> '') then
          a.setAttribute('class',aclass);

        if (Items[i].Link = '') then
          a.setAttribute('href','#')
        else
          a.setAttribute('href',Items[i].Link);

        if (Items[i].LinkTarget <> '') then
          a.setAttribute('target',Items[i].LinkTarget);

        a.addEventListener('click',@HandleDoAnchorClick);

        if doins or not Items[i].IsLink then
          li.appendChild(a);
      end
      else
      begin
        li.innerHTML := Items[i].Text;
        // take elements in account inserted as HTML text to not restore
        Items[i].FHTMLChilds := li.childElementCount;
      end;

      el := TJSHTMLElement(li);

      GetItemChildren(Items[i], el);

      AElement.appendChild(li);

      Items[i].AbsIndex := AIndex;
      inc(AIndex);

      if Style <> lsTabs then
      begin

        if (Items[i].Items.Count > 0) then
        begin
          dv := document.createElement('div');

          if Items[i].AutoCollaps then
            dv.setAttribute('class','collapse');

          dv.setAttribute('id', GetID + Items[i].ItemId);
          AElement.appendChild(dv);

          if Items[i].AutoCollaps then
          begin
            li.setAttribute(datatoggle,'collapse');
            li.setAttribute(datatarget,'#' + GetID + Items[i].ItemId);
          end;

          ul := document.createElement('ul');
          dv.appendChild(ul);

          RenderList(ul, Items[i].Items, AIndex);
        end;
      end;
    end;
end;

procedure TListControl.SetElementListClassName(const Value: TElementClassName);
begin
  FElementListClassName := Value;
  UpdateElement;
end;

procedure TListControl.SetFilter(Condition: string; CaseSensitive: boolean = true);
var
  i: integer;
  el: TJSHTMLElement;
begin
  for i := 0 to Items.Count - 1 do
  begin
     el := TJSHTMLElement(Items[i].FListElement);
     if not MatchStr(Condition, Items[i].Text, CaseSensitive) then
       el.style.setProperty('display','none');
  end;

end;

procedure TListControl.SetItemIndex(const Value: integer);
var
  i,j,v: integer;
begin
  BeginUpdate;

  v := 0;

  for i := 0 to Items.Count - 1 do
  begin
    Items[i].Active := (Value = v);
    inc(v);
    if Items[i].Items.Count > 0 then
    begin
      for j := 0 to Items[i].Items.Count - 1 do
      begin
        Items[i].Items[j].Active := (Value = v);
        inc(v);
      end;
    end;
  end;

  EndUpdate;
end;

procedure TListControl.SetItems(const Value: TListItems);
begin
  FItems := Value;
end;

procedure TListControl.SetListStyle(const Value: TListStyle);
begin
  FListStyle := Value;

  if IsUpdating then
    Exit;

  case FListStyle of
    lsPagination:
      begin
        DefaultItemClassName := 'page-item';
        DefaultItemLinkClassName := 'page-link';
        ElementListClassName := 'pagination';
      end;
    lsTabs:
      begin
        DefaultItemClassName := 'nav-item';
        DefaultItemLinkClassName := 'nav-link';
        ElementListClassName := 'nav nav-tabs';
      end;
    lsBreadcrumb:
      begin
        DefaultItemClassName := 'breadcrumb-item';
        DefaultItemLinkClassName := 'breadcrumb-link';
        ElementListClassName := 'breadcrumb';
      end;
    lsListGroup:
      begin
        DefaultItemClassName := 'list-group-item';
        DefaultItemLinkClassName := 'list-group-link';
        ElementListClassName := 'list-group';
      end;
    lsListGroupHorizontal:
      begin
        DefaultItemClassName := 'list-group-item';
        DefaultItemLinkClassName := 'list-group-link';
        ElementListClassName := 'list-group list-group-horizontal';
      end;
    lsNavPills:
      begin
        DefaultItemClassName := 'nav-item';
        DefaultItemLinkClassName := 'nav-link';
        ElementListClassName := 'nav nav-pills flex-column';
      end;
    lsNavPillsHorizontal:
      begin
        DefaultItemClassName := 'nav-item';
        DefaultItemLinkClassName := 'nav-link';
        ElementListClassName := 'nav nav-pills';
      end;
  end;
end;

procedure TListControl.UpdateElement;
var
  ul: TJSElement;
  LLabel: TJSHTMLElement;
  LHasLabel: boolean;
  idx: integer;

begin
  inherited;

  if IsUpdating then
    Exit;

  if Assigned(ElementHandle) then
  begin
    if Items.Count > 0 then
    begin
      ElementHandle.style.removeProperty(CSSBackground);

      LLabel := TJSHTMLElement(ElementHandle.firstChild);
      LHasLabel := Assigned(LLabel) and (LLabel.tagName = 'DIV') and (LLabel.getAttribute('data-design') = '1');
      if LHasLabel then
      begin
        ElementHandle.removeChild(LLabel);
      end;

      ul := ListElement;

      if Assigned(ul) then
      begin
        if Style = lsListGroup then
          Items.Store;

        while Assigned(ul.firstChild) do
          ul.removeChild(ul.firstChild);

        if ElementListClassName <> '' then
          ul.setAttribute('class', ElementListClassName);

        ElementHandle.style.removeProperty('overflow');

        idx := 0;

        RenderList(ul, Items, idx);

        if Style = lsListGroup then
          Items.Restore;
      end;
    end
    else
    begin
      if not IsLinked and (csDesigning in ComponentState) then
      begin
        RenderDesigning('TWebListControl', Container, Self, True);
      end
      else
      begin
        ul := ListElement;
        if Assigned(ul) then
          while Assigned(ul.firstChild) do
            ul.removeChild(ul.firstChild);
      end;
    end;

    // allow scrollbar on lsListGroup style control
    if (Style in [lsListGroup]) then
      TJSHTMLElement(ElementHandle).style.setProperty('overflow','auto');
  end;
end;

end.
